import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { questions as allQuestions } from '../data/questions';

const API_BASE = process.env.REACT_APP_API_URL;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtTime(seconds) {
  const s = Number(seconds ?? 0);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec}s`;
  return `${m}m ${sec}s`;
}

function getQuestionText(questionId) {
  const q = allQuestions.find((q) => q.id === questionId);
  const text = q?.question_text ?? q?.question ?? questionId;
  return text.length > 90 ? text.slice(0, 90) + '…' : text;
}

function getArchetypeLabel(archetype) {
  const map = {
    outline_4: '4-Mark Outline',
    assess_6:  '6-Mark Assess',
    data_6:    '6-Mark Data',
    assess_9:  '9-Mark Assess',
    essay_20:  '20-Mark Essay',
  };
  return map[archetype] ?? archetype;
}

// ─── Score Badge ──────────────────────────────────────────────────────────────

function ScoreBadge({ result, archetype }) {
  if (!result) return <span className="text-[#484f58] text-[10px] font-mono">—</span>;

  const score = result.awarded_mark ?? result.total ?? null;
  const maxMap = { outline_4: 4, assess_6: 6, data_6: 6, assess_9: 9, essay_20: 20 };
  const max = maxMap[archetype] ?? null;

  if (score === null) return <span className="text-[#484f58] text-[10px] font-mono">—</span>;

  const pct = max ? (score / max) * 100 : 0;
  const colour = pct >= 75 ? '#3fb950' : pct >= 50 ? '#d29922' : '#f85149';

  return (
    <span className="text-[10px] font-mono font-semibold tabular-nums" style={{ color: colour }}>
      {score}{max ? `/${max}` : ''}
    </span>
  );
}

// ─── Status Chip ──────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const cfg = {
    completed: { bg: 'bg-[#3fb950]/10 border-[#3fb950]/30 text-[#3fb950]', label: 'Marked' },
    pending:   { bg: 'bg-[#d29922]/10 border-[#d29922]/30 text-[#d29922]', label: 'Pending' },
    error:     { bg: 'bg-[#f85149]/10 border-[#f85149]/30 text-[#f85149]', label: 'Error' },
  }[status] ?? { bg: 'bg-[#30363d] border-[#30363d] text-[#8b949e]', label: status ?? '—' };

  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase tracking-wide ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
}

// ─── Practice Tab ─────────────────────────────────────────────────────────────

function PracticeTab({ getToken }) {
  const [attempts, setAttempts] = useState(null);
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/my-history/practice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load practice history');
      const data = await res.json();
      setAttempts(data.attempts ?? []);
    } catch (err) {
      setError(err.message);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-[#f85149] text-xs">{error}</p>
      </div>
    );
  }

  if (!attempts) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
      </div>
    );
  }

  if (attempts.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-[#8b949e] text-sm">No practice attempts yet.</p>
        <p className="text-[#484f58] text-xs mt-1">Select a question and click Mark to get started.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#21262d]">
      {attempts.map((a) => (
        <div key={a.attemptId} className="px-6 py-4 hover:bg-[#161b22]/50 transition-colors">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[#e6edf3] text-xs leading-snug mb-1.5">
                {getQuestionText(a.questionId)}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono">
                  {getArchetypeLabel(a.archetype)}
                </span>
                <span className="text-[#484f58] text-[9px] font-mono">{fmtDateTime(a.createdAt)}</span>
                <span className="text-[#484f58] text-[9px] font-mono">{fmtTime(a.timeSpentSeconds)}</span>
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
              <ScoreBadge result={a.markingResult} archetype={a.archetype} />
              <StatusChip status={a.markingStatus} />
            </div>
          </div>
          {a.markingResult?.justification && (
            <p className="mt-2 text-[#8b949e] text-[10px] leading-relaxed line-clamp-2">
              {a.markingResult.justification}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Mock Exams Tab ───────────────────────────────────────────────────────────

function ExamsTab({ getToken }) {
  const [exams, setExams]   = useState(null);
  const [error, setError]   = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/my-history/exams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load exam history');
      const data = await res.json();
      setExams(data.exams ?? []);
    } catch (err) {
      setError(err.message);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const toggleExam = (examId) => setExpanded((prev) => ({ ...prev, [examId]: !prev[examId] }));

  if (error) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-[#f85149] text-xs">{error}</p>
      </div>
    );
  }

  if (!exams) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
      </div>
    );
  }

  if (exams.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-[#8b949e] text-sm">No mock exams yet.</p>
        <p className="text-[#484f58] text-xs mt-1">Start a Mock Exam from the sidebar to begin.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#21262d]">
      {exams.map((exam, i) => {
        const isOpen = expanded[exam.examId];
        const allDone = exam.questions.every(
          (q) => q.markingStatus === 'completed' || q.markingStatus === 'error',
        );
        const maxTotal = exam.questions.length * 4;

        return (
          <div key={exam.examId}>
            <button
              onClick={() => toggleExam(exam.examId)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#161b22]/50 transition-colors text-left"
            >
              <div>
                <p className="text-[#e6edf3] text-xs font-semibold mb-1">
                  Mock Exam #{exams.length - i}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#484f58] text-[9px] font-mono">{fmtDateTime(exam.submittedAt)}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono">
                    {exam.questions.length} questions
                  </span>
                  {!allDone && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#d29922]/30 text-[#d29922] bg-[#d29922]/10 font-mono">
                      Marking…
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {exam.totalScore !== null && (
                  <div className="text-right">
                    <p className="text-[#e6edf3] text-base font-bold font-mono tabular-nums">
                      {exam.totalScore}
                      <span className="text-[#484f58] text-xs">/{maxTotal}</span>
                    </p>
                    <p className="text-[#8b949e] text-[9px] font-mono">total</p>
                  </div>
                )}
                <svg
                  className={`w-3.5 h-3.5 text-[#8b949e] transition-transform flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {isOpen && (
              <div className="bg-[#0d1117] border-t border-[#21262d]">
                {exam.questions.map((q, qi) => (
                  <div
                    key={q.attemptId}
                    className="px-8 py-3 flex items-start justify-between gap-4 border-b border-[#21262d] last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[#8b949e] text-[10px] mb-0.5 font-mono">Q{qi + 1}</p>
                      <p className="text-[#e6edf3] text-xs leading-snug">
                        {getQuestionText(q.questionId)}
                      </p>
                      <p className="text-[#484f58] text-[9px] font-mono mt-1">{fmtTime(q.timeSpentSeconds)}</p>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                      <ScoreBadge result={q.markingResult} archetype="outline_4" />
                      <StatusChip status={q.markingStatus} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Quiz Topic Label ─────────────────────────────────────────────────────────

const QUIZ_TOPIC_DISPLAY = {
  water_carbon:      'Water & Carbon',
  hazards:           'Hazards',
  deserts:           'Hot Deserts',
  changing_places:   'Changing Places',
  global_systems:    'Global Systems',
  resource_security: 'Resource Security',
  random_mix:          'Random Mix',
  random_mix_paper_1:  'Random Mix · Paper 1',
  random_mix_paper_2:  'Random Mix · Paper 2',
};

// ─── Quiz Tab ─────────────────────────────────────────────────────────────────

function QuizTab({ getToken }) {
  const [quizzes, setQuizzes] = useState(null);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/my-history/quiz`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load quiz history');
      const data = await res.json();
      setQuizzes(data.quizzes ?? []);
    } catch (err) {
      setError(err.message);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-[#f85149] text-xs">{error}</p>
      </div>
    );
  }

  if (!quizzes) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
      </div>
    );
  }

  if (quizzes.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-[#8b949e] text-sm">No quiz attempts yet.</p>
        <p className="text-[#484f58] text-xs mt-1">Start a Quiz from the sidebar to begin.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[#21262d]">
      {quizzes.map((q) => {
        const pct = Math.round((q.score / q.totalQuestions) * 100);
        const colour = pct >= 80 ? '#3fb950' : pct >= 60 ? '#d29922' : '#f85149';
        const topicLabel = QUIZ_TOPIC_DISPLAY[q.topic] ?? q.topic;

        return (
          <div key={q.id} className="px-6 py-4 hover:bg-[#161b22]/50 transition-colors">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono">
                    MCQ Quiz
                  </span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono">
                    {topicLabel}
                  </span>
                </div>
                <p className="text-[#484f58] text-[9px] font-mono">{fmtDateTime(q.createdAt)}</p>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="font-bold font-mono tabular-nums text-base" style={{ color: colour }}>
                  {q.score}
                  <span className="text-[#484f58] text-xs">/{q.totalQuestions}</span>
                </p>
                <p className="text-[#484f58] text-[9px] font-mono">{pct}%</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HistoryHub({ onClose }) {
  const { getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab]  = useState('practice');

  const TABS = [
    { id: 'practice', label: 'Practice Questions' },
    { id: 'exams',    label: 'Mock Exams' },
    { id: 'quiz',     label: 'Quiz Results' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#0d1117]/80 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-[#0d1117] border border-[#30363d] rounded-xl w-full max-w-2xl shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#30363d]">
          <div>
            <h2 className="text-[#e6edf3] text-sm font-semibold">Results Ledger</h2>
            <p className="text-[#8b949e] text-xs mt-0.5">Your marked attempts and exam history</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#161b22] transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#30363d]">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-xs font-semibold font-mono transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-[#58a6ff] text-[#58a6ff]'
                  : 'border-transparent text-[#8b949e] hover:text-[#e6edf3]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto">
          {activeTab === 'practice' && (
            <PracticeTab getToken={getAccessTokenSilently} />
          )}
          {activeTab === 'exams' && (
            <ExamsTab getToken={getAccessTokenSilently} />
          )}
          {activeTab === 'quiz' && (
            <QuizTab getToken={getAccessTokenSilently} />
          )}
        </div>
      </div>
    </div>
  );
}
